import * as React from 'react';
import moment from 'moment';
import Loader from 'react-loader';
import { connect } from 'react-redux';
import DateTime from 'react-datetime';
import { Row, Col, Button } from 'react-bootstrap';
import { Translate } from '../../shared/LanguageService';
import { assign } from '../../shared/Assign';
import Calendar from '../calendar/Calendar';
import { formatTimeTwoDigitAs, toMinMaxTime } from '../calendar/calendarTimeUtils';
import IRegion from '../regions/types/region';
import ICalendar from '../calendar/ICalendar';
import IWorkingHour from '../calendar/IWorkingHour';
import ICalendarItem, { ICalendarItemUserType } from '../calendar/Types';
import { limitEventEndToStartDay } from '../calendar/calendarEventUtils';
import * as Site from '../../shared/Site';
import notify from '../../shared/feedback/notifications';
import StylingCalendarApi, { IStylingUser } from './StylingCalendarApi';
import StylingUserCalendarComponent from '../stylingUserPersonalCalendars/StylingUserCalendarComponent';

interface IActions {
    getMultiplePhotographerCalendars: (ids: number[], from: any, to: any) => ICalendar[];
    createPhotographerCalendarItem: (item: any) => Promise<any>;
    deletePhotographerCalendarItem: (item: any) => Promise<any>;
    updatePhotographerCalendarItem: (item: any) => Promise<any>;
    updatePhotographerCalendarWorkingHours: (
        photographerId: number,
        se360WorkingHours: any[],
        scandphotosWorkingHours: any[]
    ) => Promise<any>;
}

interface IProps {
    regions: IRegion[];
    calendars: ICalendar[];
    user: any;
    actions: IActions;
}

interface IState {
    stylingUsers: IStylingUser[];
    calendars: ICalendar[];
    calendarSettings: any;
    selectedDate: any;
    selectedStylingUser: number;
    from: any;
    to: any;
    loading: boolean;
}

class StylingCalendarsPage extends React.Component<IProps, IState> {
    constructor(props: IProps, context: any) {
        super(props, context);
        this.onDateChange = this.onDateChange.bind(this);

        let calendarSettings: any = {};

        this.state = {
            calendars: [],
            calendarSettings: calendarSettings,
            selectedDate: moment(),
            stylingUsers: [],
            selectedStylingUser: -1,
            loading: false,
            from: moment(),
            to: moment()
        };
    }

    componentDidMount() {
        this.getStylingUsersByFirm();
    }

    getStylingUsersByFirm = () => {
        StylingCalendarApi.getStylingUsersByFirm().then((result: IStylingUser[]) => {
            this.loadCalendars(result);
            this.setState({
                stylingUsers: result
            });
        });
    };

    private loadCalendars(result: IStylingUser[]) {
        this.setState({ loading: true });
        StylingCalendarApi.getStylingCalendars(
            result.map((user) => {
                return user.id;
            }),
            this.state.selectedStylingUser === -1 ? this.state.selectedDate : this.state.from,
            this.state.selectedStylingUser === -1 ? this.state.selectedDate : this.state.to
        )
            .then((calendars) => {
                this.setState({
                    calendars: this.createCalendars(assign([], calendars))
                });
                this.setState({ loading: false });
            })
            .catch(() => {
                this.setState({ loading: false });
            });
    }

    getDefaultCalendarSettings() {
        let calendarSettings: any = {
            selectable: false,
            eventRender: (event, element) => {
                element.append(
                    `<div style="font-size:10px;font-weight: bold;">` +
                        `<span style="font-size:11px">${event.description}</span>` +
                        `</br>` +
                        `<span>${event.product}</span>` +
                        `</div>`
                );
            },
            slotLabelFormat: 'HH:mm',
            timeFormat: 'HH:mm',
            minTime: '07:00:00',
            maxTime: '18:00:00',
            firstDay: 0,
            defaultView: 'photographerView',
            highlightToday: false,
            disableResizing: false,
            views: {
                photographerView: {
                    type: 'agenda',
                    duration: { days: 7 }
                }
            },
            header: {
                left: '',
                center: '',
                right: ''
            }
        };

        return calendarSettings;
    }

    createCalendars(calendars: any, date?: any) {
        let currentDate = date || this.state.selectedDate;
        let filteredCalendars = calendars;

        filteredCalendars = calendars.filter((calendar) => {
            let matchingDay = calendar.se360WorkingHours.find((item: IWorkingHour) => {
                return item.day === currentDate.day();
            });
            return matchingDay && matchingDay.isAvailable;
        });

        return this.sliceCalendars(filteredCalendars, currentDate || null);
    }

    sliceCalendars(photographerCalendars: any, date?: any) {
        let sliced = [];
        let calendarList = [];

        for (let i = 0; i < photographerCalendars.length; i++) {
            if (i > 0 && i % 5 === 0) {
                calendarList.push(this.createCalendar(sliced, date || null));
                sliced = [];
            }
            sliced.push(photographerCalendars[i]);
        }
        if (sliced.length > 0) {
            calendarList.push(this.createCalendar(sliced, date || null));
        }
        return calendarList;
    }

    createCalendar(calendars: any, selectedDate?: any) {
        let settings = this.getDefaultCalendarSettings();

        const { apiUrl } = window.appConfig;

        // Set amount of 'days' corresponding to nr of photographerCalendars
        settings.views.photographerView.duration = {
            days: calendars.length
        };
        let currentDate = selectedDate || this.state.selectedDate;
        settings.defaultDate =
            currentDate.day() === 0
                ? currentDate.format()
                : moment(currentDate.format()).isoWeekday(0).format();

        settings.workingHours = [];
        let events = [];
        let names = [];
        settings.se360WorkingHours = [];

        // Due to fullcalender does not work with locale, has not included locale files
        moment.locale('en');
        let minTime = 7;
        let maxTime = 18;
        calendars.forEach((calendar: any, index: number) => {
            let userQuery = this.state.stylingUsers.filter((p) => {
                return p.id === calendar.stylingUserId;
            });

            let user =
                userQuery.length === 1
                    ? userQuery[0]
                    : { name: '', id: -1, profileImageReference: null };

            settings.se360WorkingHours = calendar.se360WorkingHours;

            let se360WorkingHours = calendar.se360WorkingHours.filter((item: IWorkingHour) => {
                return item.day === currentDate.day();
            });

            const currentWorkingHours = se360WorkingHours;
            currentWorkingHours.forEach((item: IWorkingHour) => {
                // +/- 1 to get a margin
                const itemStartHour = moment(item.start).hours() - 1;
                const itemEndHour = moment(item.end).hours() + 1;

                minTime = itemStartHour < minTime ? itemStartHour : minTime;
                maxTime = itemEndHour > maxTime ? itemEndHour : maxTime;
            });

            calendar.items
                .filter((item: ICalendarItem) => {
                    if (this.state.selectedStylingUser === -1) {
                        return moment(item.start).startOf('day').isSame(currentDate.startOf('day'));
                    }
                    return 1;
                })
                .forEach((item: ICalendarItem) => {
                    let event = assign({}, item);

                    if (this.state.selectedStylingUser === -1) {
                        event.editable = false;
                    }

                    event.start = moment.parseZone(item.start as any);
                    event.end = moment.parseZone(item.end as any);

                    event.originalStart = event.start.format();
                    event.originalEnd = event.end.format();

                    event.end = limitEventEndToStartDay(event.start, event.end);

                    event.start =
                        event.start.weekday() === index
                            ? event.start.format()
                            : event.start.weekday(index).format();
                    event.end =
                        event.end.weekday() === index
                            ? event.end.format()
                            : event.end.weekday(index).format();

                    // +/- 1 to get a margin
                    const eventStartHour = moment(event.start).hours() - 1;
                    const eventEndHour = moment(event.end).hours() + 1;

                    minTime = eventStartHour < minTime ? eventStartHour : minTime;
                    maxTime = eventEndHour > maxTime ? eventEndHour : maxTime;

                    if (item.site) {
                        event.className = [Site.getOrderCssClass(item.site)];
                    }
                    events.push(event);
                });

            let name = '<div class="photographer-header">';

            if (se360WorkingHours.length > 0 && se360WorkingHours[0].isAvailable === true) {
                let w = assign({}, se360WorkingHours[0]);
                w.day = index;
                settings.workingHours.push(w);
            }

            let profileImageUrl = user.profileImageReference
                ? `${apiUrl}/file/${user.profileImageReference}`
                : 'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyB2ZXJzaW9uPSIxLjEiIHZpZXdCb3g9IjAgMCAyNTAgMjUwIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgogPGcgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoLTI2Ni40MyAtNDAxLjY1KSI+CiAgPGcgdHJhbnNmb3JtPSJtYXRyaXgoLjY4MzMzIDAgMCAuNjgzMzMgNTgxLjk0IDU1MC43NykiIGZpbGw9IiNEREREREQiPgogICA8cmVjdCB4PSItNDYxLjczIiB5PSItMjE4LjIzIiB3aWR0aD0iMzY1Ljg1IiBoZWlnaHQ9IjM2NS44NSIgcng9IjAiIHJ5PSIwIiBjb2xvcj0iIzAwMDAwMCIgZmlsbD0iI0RERERERCIvPgogIDwvZz4KICA8ZyB0cmFuc2Zvcm09Im1hdHJpeCguMzQ5MiAwIDAgLjM0OTIgMjg0LjU3IDQxOS43OSkiIGZpbGw9IiNGRkYiPgogICA8ZyBmaWxsPSIjRkZGIj4KICAgIDxwYXRoIGQ9Im0zMDYgMzI1Ljk5YzkwLjU2LTAuMDEgMTIzLjE1LTkwLjY4IDEzMS42OC0xNjUuMTcgMTAuNTEtOTEuNzYtMzIuODgtMTYwLjgyLTEzMS42OC0xNjAuODItOTguNzggMC0xNDIuMTkgNjkuMDU1LTEzMS42OCAxNjAuODIgOC41NCA3NC40ODQgNDEuMTE0IDE2NS4xOCAxMzEuNjggMTY1LjE3eiIvPgogICAgPHBhdGggZD0ibTU1MC45OCA1NDEuOTFjLTAuOTktMjguOTA0LTQuMzc3LTU3LjkzOS05LjQyMS04Ni4zOTMtNi4xMTEtMzQuNDY5LTEzLjg4OS04NS4wMDItNDMuOTgzLTEwNy40Ni0xNy40MDQtMTIuOTg4LTM5Ljk0MS0xNy4yNDktNTkuODY1LTI1LjA4MS05LjY5Ny0zLjgxLTE4LjM4NC03LjU5NC0yNi41MzctMTEuOTAxLTI3LjUxOCAzMC4xNzYtNjMuNCA0NS45NjItMTA1LjE5IDQ1Ljk2NC00MS43NzQgMC03Ny42NTItMTUuNzg2LTEwNS4xNy00NS45NjQtOC4xNTMgNC4zMDgtMTYuODQgOC4wOTMtMjYuNTM3IDExLjkwMS0xOS45MjQgNy44MzItNDIuNDYxIDEyLjA5Mi01OS44NjMgMjUuMDgxLTMwLjA5NiAyMi40NjMtMzcuODczIDcyLjk5Ni00My45ODMgMTA3LjQ2LTUuMDQ1IDI4LjQ1NC04LjQzMyA1Ny40ODktOS40MjIgODYuMzkzLTAuNzY2IDIyLjM4NyAxMC4yODggMjUuNTI1IDI5LjAxNyAzMi4yODQgMjMuNDUzIDguNDU4IDQ3LjY2NiAxNC43MzcgNzIuMDQxIDE5Ljg4NCA0Ny4wNzcgOS45NDEgOTUuNjAzIDE3LjU4MiAxNDMuOTIgMTcuOTI0IDQ4LjMxOC0wLjM0MyA5Ni44NDQtNy45ODMgMTQzLjkyLTE3LjkyNCAyNC4zNzUtNS4xNDUgNDguNTktMTEuNDI0IDcyLjA0MS0xOS44ODQgMTguNzM2LTYuNzU3IDI5Ljc4OS05Ljg5NSAyOS4wMjMtMzIuMjg0eiIvPgogICA8L2c+CiAgPC9nPgogPC9nPgo8L3N2Zz4K';

            name += `<div class="photographer-image" style="background-image: url('${profileImageUrl}')"></div>
                     <div class="photographer-info">
                         <div class="photographer-name">${user.name}</div>
                     </div>`;
            names.push(name);
        });

        const minMaxTime = toMinMaxTime(minTime, maxTime);
        settings.minTime = minMaxTime.minTime;
        settings.maxTime = minMaxTime.maxTime;

        // Due to fullcalender does not work with locale, has not included locale files
        moment.locale(this.props.user.details.languageKey);

        events = events.filter((e) => {
            return e.start !== null && e.end !== null;
        });
        settings.eventSources = [
            {
                events: events
            }
        ];

        settings.dayNames = names;

        return settings;
    }

    getWorkingHoursAsString(workingHour: IWorkingHour) {
        const startHours = moment(workingHour.start).hours();
        const startMinutes = moment(workingHour.start).minutes();
        const endHour = moment(workingHour.end).hours();
        const endMinutes = moment(workingHour.end).minutes();

        let start = formatTimeTwoDigitAs(startHours) + ':' + formatTimeTwoDigitAs(startMinutes);
        let end = formatTimeTwoDigitAs(endHour) + ':' + formatTimeTwoDigitAs(endMinutes);

        return start + ' - ' + end;
    }

    onDateChange(value: any) {
        let selectedDate = this.state.selectedDate;
        if (!value.isSame(selectedDate)) {
            this.setState(
                {
                    selectedDate: value,
                    calendars: [],
                    loading: true
                },
                () => {
                    this.loadCalendars(this.state.stylingUsers);
                }
            );
        }
    }

    stylingUserChange(value: any) {
        let val = value.target.value;
        this.setState(
            {
                calendars: [],
                loading: true,
                selectedStylingUser: val.id,
                stylingUsers: [val],
                from: moment(this.state.selectedDate).clone().startOf('week'),
                to: moment(this.state.selectedDate).clone().endOf('week')
            },
            () => {
                this.loadCalendars(this.state.stylingUsers);
            }
        );
    }

    selectStylingUser = (stylingUserName: string) => {
        const stylingUser = this.state.stylingUsers.find((x) => x.name === stylingUserName);
        this.stylingUserChange({ target: { value: stylingUser } });
    };

    resetSelectedStylingUser = () => {
        this.setState(
            {
                calendars: [],
                loading: true,
                selectedStylingUser: -1,
                stylingUsers: []
            },
            () => {
                this.getStylingUsersByFirm();
            }
        );
    };

    onCreated = (event: any) => {
        event.stylingUserId = this.state.selectedStylingUser;
        StylingCalendarApi.createStylingUserCalendarItem(event).then(() => {
            notify.success(Translate('calendars/photographer-items/update/toastr/success'));
        });
        this.loadCalendars(this.state.stylingUsers);
    };

    onDeleted = (event: any) => {
        StylingCalendarApi.deleteStylingUserCalendarItem(event).then(() => {
            notify.success(Translate('calendars/photographer-items/update/toastr/success'));
        });
        this.loadCalendars(this.state.stylingUsers);
    };

    onUpdated = (event: any) => {
        event.stylingUserId = this.state.selectedStylingUser;
        StylingCalendarApi.updateStylingUserCalendarItem(event).then(() => {
            notify.success(Translate('calendars/photographer-items/update/toastr/success'));
        });
        this.loadCalendars(this.state.stylingUsers);
    };

    renderIndividualStylistCalendar = () => {
        return (
            <Row>
                <Col sm={5} md={4} className="col-xlg-3"></Col>
                <Col sm={7} md={8} className="col-xlg-9">
                    <div>
                        <Button onClick={this.resetSelectedStylingUser}>
                            <i className="material-icons">arrow_back</i>
                        </Button>
                        <h2 className="center">{this.state.stylingUsers[0].name}</h2>
                        <StylingUserCalendarComponent
                            calendar={this.state.calendars[0]}
                            user={this.props.user}
                            onCreated={this.onCreated}
                            onDeleted={this.onDeleted}
                            onUpdated={this.onUpdated}
                        />
                    </div>
                    {this.state.loading && (
                        <div style={{ height: '50vh' }}>
                            <Loader loaded={!this.state.loading}></Loader>
                        </div>
                    )}
                </Col>
            </Row>
        );
    };

    renderAllStylistsCalendars = () => {
        return (
            <Row>
                <Col sm={5} md={4} className="col-xlg-3">
                    <div>
                        <DateTime
                            locale={moment.locale()}
                            input={false}
                            timeFormat={false}
                            onChange={this.onDateChange}
                            value={this.state.selectedDate}
                        />
                    </div>
                </Col>
                <Col sm={7} md={8} className="col-xlg-9">
                    <div>
                        {this.state.calendars.map((calendarSettings: any, index: number) => (
                            <Calendar
                                calendarItemUserType={ICalendarItemUserType.StylingUser}
                                key={'calendar-' + index}
                                settings={calendarSettings}
                                calendarItems={null}
                                workingHours={null}
                                onUpdated={(event) => {}}
                                onChange={(items: ICalendarItem[]) => {}}
                                canUnbook={false}
                                onRangeChanged={(from: any, to: any) => {
                                    this.setState({
                                        from,
                                        to
                                    });
                                    this.loadCalendars(this.state.stylingUsers);
                                }}
                                onDaynameClick={this.selectStylingUser}
                                selectedDate={this.state.selectedDate}
                                user={this.props.user}
                            />
                        ))}
                    </div>
                    {this.state.loading && (
                        <div style={{ height: '50vh' }}>
                            <Loader loaded={!this.state.loading}></Loader>
                        </div>
                    )}
                </Col>
            </Row>
        );
    };

    render() {
        let shouldShowIndividualStylistCalendar =
            this.state.selectedStylingUser !== -1 &&
            this.state.calendars &&
            this.state.calendars.length > 0;

        return shouldShowIndividualStylistCalendar
            ? this.renderIndividualStylistCalendar()
            : this.renderAllStylistsCalendars();
    }
}

export default connect(
    (state: any, ownProps: any) => {
        return {
            user: state.user
        };
    },
    (dispatch: any) => {
        return {
            actions: {}
        };
    }
)(StylingCalendarsPage);
